import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faGraduationCap, 
  faHandshake, 
  faBuildingUser 
} from '@fortawesome/free-solid-svg-icons';

const SectionContainer = styled.section`
  padding: 5rem 2rem;
  background: white;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  color: #2d3748;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: #4a5568;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const UseCasesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
`;

const UseCase = styled.div`
  background: #f7fafc;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-5px);
  }
`;

const IconContainer = styled.div`
  width: 60px;
  height: 60px;
  background: #ebf8ff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: #3182ce;
  font-size: 1.8rem;
`;

const UseCaseTitle = styled.h3`
  font-size: 1.25rem;
  color: #2d3748;
  margin-bottom: 1rem;
`;

const UseCaseDescription = styled.p`
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BenefitItem = styled.li`
  color: #4a5568;
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
  position: relative;

  &:before {
    content: "•";
    color: #3182ce;
    position: absolute;
    left: 0;
  }
`;

const useCases = [
  {
    icon: faUsers,
    title: "Virtual Events & Conferences",
    description: "Transform your online events into engaging networking experiences.",
    benefits: [
      "Facilitate spontaneous connections",
      "Increase attendee engagement",
      "Track networking metrics",
      "Create memorable experiences"
    ]
  },
  {
    icon: faGraduationCap,
    title: "Educational Institutions",
    description: "Connect students and faculty in a fun and interactive way.",
    benefits: [
      "Foster student collaboration",
      "Enable mentor matching",
      "Support virtual orientation",
      "Build campus community"
    ]
  },
  {
    icon: faHandshake,
    title: "Professional Networking",
    description: "Revolutionize how professionals connect and collaborate.",
    benefits: [
      "Speed networking sessions",
      "Industry matchmaking",
      "Cross-department connections",
      "Skill-based matching"
    ]
  },
  {
    icon: faBuildingUser,
    title: "Corporate Team Building",
    description: "Strengthen team bonds in remote and hybrid workplaces.",
    benefits: [
      "Break down silos",
      "Improve company culture",
      "Facilitate remote onboarding",
      "Boost employee engagement"
    ]
  }
];

export const UseCasesSection: React.FC = () => {
  return (
    <SectionContainer>
      <Content>
        <Header>
          <Title>Perfect for Every Connection</Title>
          <Subtitle>
            Discover how SpinToConnect adapts to various scenarios, making networking 
            effortless and engaging across different industries and use cases.
          </Subtitle>
        </Header>
        <UseCasesGrid>
          {useCases.map((useCase, index) => (
            <UseCase key={index}>
              <IconContainer>
                <FontAwesomeIcon icon={useCase.icon} />
              </IconContainer>
              <UseCaseTitle>{useCase.title}</UseCaseTitle>
              <UseCaseDescription>{useCase.description}</UseCaseDescription>
              <BenefitsList>
                {useCase.benefits.map((benefit, benefitIndex) => (
                  <BenefitItem key={benefitIndex}>{benefit}</BenefitItem>
                ))}
              </BenefitsList>
            </UseCase>
          ))}
        </UseCasesGrid>
      </Content>
    </SectionContainer>
  );
}; 